<script setup>
import { useApolloClient } from '@vue/apollo-composable';
import { computed, ref, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { fetchProjects } from '~/api/issues';
import ErrorState from '~/common/components/ErrorState.vue';
import MarkdownRenderer from '~/common/components/MarkdownRenderer.vue';
import { listParam, parseQueryParams, syncQueryParams } from '~/common/QueryParams';
import { useGitlabHostname } from '~/common/useGitlabHostname';
import { useVueData } from '~/common/useVueData';
import { IssuesQueryDocument } from '~/graphql/types';

import DisplaySection from './issues-page/DisplaySection.vue';
import { fetchPrimaryLanguages } from './issues-page/languages';
import { loadPlatformIssues } from './issues-page/platformIssues';
import { buildProjectDisplayNames, projectPath } from './issues-page/projects';
import SorterSection from './issues-page/SorterSection.vue';
import { getVariant } from './issues-page/variants';

const busy = ref(true);
const issues = ref([]);
const totalCount = ref(0);
const error = ref(false);
const progress = ref(0);
const currentPage = ref(1);
const sortBy = ref([
  {
    key: 'createdAt',
    order: 'asc',
  },
]);

const { client } = useApolloClient();
const { gitlabGroups } = useVueData();
const gitlabHostname = useGitlabHostname();
const route = useRoute();
const router = useRouter();

const variant = computed(() => getVariant(route.query.variant));

const shownLabels = ref([]);
const labelsToQuery = ref([]);
const selectedLabels = ref([]);
const selectedProjects = ref([]);
const selectedLanguages = ref([]);
const projectLanguages = ref(new Map());
const platformProjects = ref([]);

const projectDisplayNames = computed(() => buildProjectDisplayNames(
  route.query.source === 'gitlab'
    ? issues.value.map(projectPath)
    : platformProjects.value,
));

const availableProjects = computed(
  () => [...new Set(projectDisplayNames.value.values())].sort((a, b) => a.localeCompare(b)),
);

const activeProjects = computed({
  get: () => selectedProjects.value.filter(project => availableProjects.value.includes(project)),
  set: (projects) => {
    selectedProjects.value = projects;
  },
});

const availableLanguages = computed(
  () => [...new Set(projectLanguages.value.values())].sort((a, b) => a.localeCompare(b)),
);

const activeLanguages = computed({
  get: () => selectedLanguages.value.filter(language => availableLanguages.value.includes(language)),
  set: (languages) => {
    selectedLanguages.value = languages;
  },
});

const queryParams = () => ({
  labels: listParam({ allowed: shownLabels.value, fallback: shownLabels.value }),
  projects: listParam(),
  languages: listParam(),
});

const syncFromQuery = () => {
  const { labels, projects, languages } = parseQueryParams(route.query, queryParams());

  selectedLabels.value = labels;
  selectedProjects.value = projects;
  selectedLanguages.value = languages;
};

const updateURL = () => {
  syncQueryParams(router, queryParams(), {
    labels: selectedLabels.value,
    projects: selectedProjects.value,
    languages: selectedLanguages.value,
  });
};

const MAX_RETRIES = 1;

const queryIssues = async (variables, attempt = 0) => {
  try {
    return await client.query({
      query: IssuesQueryDocument,
      variables,
      fetchPolicy: 'network-only',
    });
  } catch (e) {
    if (attempt >= MAX_RETRIES) {
      throw e;
    }

    return queryIssues(variables, attempt + 1);
  }
};

const loadIssuesFor = async (fullPath, labelName) => {
  const loaded = [];
  let hasNextPage = true;
  let after = null;

  while (hasNextPage) {
    let result;
    try {
      result = await queryIssues({ fullPath, labelName, after });
    } catch (e) {
      error.value = true;
      // eslint-disable-next-line no-console
      console.error('Failed to load issues for label:', labelName, 'in group:', fullPath, e);

      return loaded;
    }

    const issuesData = result.data.group?.issues;
    if (!issuesData) {
      return loaded;
    }

    loaded.push(...issuesData.nodes);

    ({ endCursor: after, hasNextPage } = issuesData.pageInfo);
  }

  return loaded;
};

const loadAllIssues = async () => {
  progress.value = 10;

  const requests = labelsToQuery.value.flatMap(
    labelName => gitlabGroups.map(fullPath => ({ fullPath, labelName })),
  );
  let completed = 0;

  const queryPromises = requests.map(async ({ fullPath, labelName }) => {
    const loaded = await loadIssuesFor(fullPath, labelName);

    completed += 1;
    progress.value = Math.round(10 + ((90 * completed) / requests.length));

    return loaded;
  });

  try {
    const results = await Promise.all(queryPromises);

    const allIssues = results.flat();

    const uniqueIssues = [];
    const seen = new Set();

    for (const issue of allIssues) {
      if (!seen.has(issue.webUrl)) {
        seen.add(issue.webUrl);
        uniqueIssues.push(issue);
      }
    }

    issues.value = uniqueIssues;
    busy.value = false;
    progress.value = 100;
  } finally {
    busy.value = false;
  }
};

const calculateEffectiveLabels = () => {
  if (selectedLabels.value.length === 0) {
    return [];
  }

  if (selectedLabels.value.length === shownLabels.value.length) {
    return undefined;
  }

  return selectedLabels.value;
};

const matchesProjectFilters = path => (
  (activeProjects.value.length === 0 || activeProjects.value.includes(projectDisplayNames.value.get(path)))
  && (activeLanguages.value.length === 0 || activeLanguages.value.includes(projectLanguages.value.get(path)))
);

const calculateEffectiveProjects = () => {
  if (activeProjects.value.length === 0 && activeLanguages.value.length === 0) {
    return undefined;
  }

  return platformProjects.value.filter(matchesProjectFilters);
};

const SORT_PARAMS = {
  createdAt: { asc: 'created_asc', desc: 'created_desc' },
  title: { asc: 'title_asc', desc: 'title_desc' },
};

const toSortParam = ([sort]) => (sort ? SORT_PARAMS[sort.key][sort.order] : 'created_asc');

let isInitializing = false;
let latestRequest = 0;

const loadFromPlatform = async () => {
  const request = ++latestRequest;
  const labels = calculateEffectiveLabels();
  const projects = calculateEffectiveProjects();
  const sort = toSortParam(sortBy.value);
  const page = currentPage.value;

  error.value = false;

  if (labels?.length === 0 || projects?.length === 0) {
    issues.value = [];
    totalCount.value = 0;
    busy.value = false;
    progress.value = 100;

    return;
  }

  busy.value = true;
  progress.value = 10;

  try {
    const result = await loadPlatformIssues(gitlabHostname, {
      labels,
      projects,
      sort,
      page,
    });

    if (request === latestRequest) {
      issues.value = result.issues;
      totalCount.value = result.totalCount;
      progress.value = 100;
    }
  } catch (e) {
    if (request === latestRequest) {
      error.value = true;
    }

    // eslint-disable-next-line no-console
    console.error('Failed to load issues from the platform:', e);
  } finally {
    if (request === latestRequest) {
      busy.value = false;
    }
  }
};

const loadProjectLanguages = async () => {
  try {
    projectLanguages.value = await fetchPrimaryLanguages(client, [...projectDisplayNames.value.keys()]);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to load project languages:', e);
  }
};

const initializeForVariant = async () => {
  isInitializing = true;
  busy.value = true;
  error.value = false;
  progress.value = 0;
  issues.value = [];
  totalCount.value = 0;
  currentPage.value = 1;
  sortBy.value = [{ key: 'createdAt', order: 'asc' }];
  platformProjects.value = [];
  projectLanguages.value = new Map();

  shownLabels.value = [...variant.value.shownLabels];
  labelsToQuery.value = [...variant.value.labelsToQuery];

  syncFromQuery();

  if (route.query.source === 'gitlab') {
    await loadAllIssues();
    await loadProjectLanguages();
    isInitializing = false;
  } else {
    try {
      platformProjects.value = await fetchProjects();
    } catch (e) {
      error.value = true;
      busy.value = false;
      // eslint-disable-next-line no-console
      console.error('Failed to load projects from the platform:', e);

      return;
    }

    await loadProjectLanguages();
    isInitializing = false;
    await loadFromPlatform();
  }
};

const resetPageAndFetch = async () => {
  if (isInitializing || route.query.source === 'gitlab') {
    return;
  }

  if (currentPage.value !== 1) {
    currentPage.value = 1;
  } else {
    await loadFromPlatform();
  }
};

watch([() => route.query.variant, () => route.query.source], () => {
  initializeForVariant();
});

watch([selectedLabels, selectedProjects, selectedLanguages], async () => {
  if (isInitializing) {
    return;
  }

  updateURL();
  await resetPageAndFetch();
});

watch(sortBy, async () => {
  await resetPageAndFetch();
});

watch(currentPage, async () => {
  if (isInitializing || route.query.source === 'gitlab') {
    return;
  }

  await loadFromPlatform();
});

const filteredIssues = computed(() => {
  const allLabelsSelected = selectedLabels.value.length === shownLabels.value.length;
  const selectedLabelsSet = new Set(selectedLabels.value);

  const matchesLabels = issue => allLabelsSelected
    || issue.labels.nodes.some(label => selectedLabelsSet.has(label.title));

  return issues.value.filter(
    issue => matchesLabels(issue) && matchesProjectFilters(projectPath(issue)),
  );
});

onMounted(() => {
  initializeForVariant();
});
</script>

<template>
  <div>
    <MarkdownRenderer :markdown-content="variant.detailsMarkdown" />

    <SorterSection
      v-model:selected-labels="selectedLabels"
      v-model:selected-projects="activeProjects"
      v-model:selected-languages="activeLanguages"
      :available-projects="availableProjects"
      :available-languages="availableLanguages"
      :shown-labels="shownLabels"
    />

    <ErrorState v-if="error" />

    <DisplaySection
      v-model:current-page="currentPage"
      v-model:sort-by="sortBy"
      :busy="busy"
      :issues="filteredIssues"
      :progress="progress"
      :shown-labels="shownLabels"
      :total-rows="route.query.source === 'gitlab' ? null : totalCount"
    />
  </div>
</template>
